import React, { useState } from 'react';
import { Eye, ExternalLink } from 'lucide-react';
import { contactWhatsApp } from '../utils/buttonActions';
import SectionHeader from './SectionHeader';
import IndustrialButton from './IndustrialButton';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const categories = ['All', 'Flex Printing', 'Vinyl Graphics', 'Shop Signage', 'Vehicle Wraps'];

  const projects = [
    {
      id: 1,
      title: 'HD Automotive Signage',
      category: 'Shop Signage',
      image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=720&h=560&fit=crop',
      description: 'Service-center frontage with structural signage and high-contrast brand panels.',
      client: 'HD Automotive',
      metric: '3 day install',
    },
    {
      id: 2,
      title: 'Restaurant Menu Board',
      category: 'Flex Printing',
      image: 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=720&h=560&fit=crop',
      description: 'Weather-resistant menu displays and promotional exterior flex prints.',
      client: 'Flaming Kitchen',
      metric: '1440 DPI',
    },
    {
      id: 3,
      title: 'Corporate Vehicle Wrap',
      category: 'Vehicle Wraps',
      image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=720&h=560&fit=crop',
      description: 'Full vehicle branding with durable vinyl, lamination, and clean edge finishing.',
      client: 'Local Delivery Service',
      metric: '5+ year vinyl',
    },
    {
      id: 4,
      title: 'Retail Store Front',
      category: 'Shop Signage',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=720&h=560&fit=crop',
      description: 'Illuminated storefront identity system with bold day-and-night visibility.',
      client: 'Fashion Retail Chain',
      metric: 'LED ready',
    },
    {
      id: 5,
      title: 'Event Banner Display',
      category: 'Flex Printing',
      image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=720&h=560&fit=crop',
      description: 'Large event graphics built for speed, distance readability, and repeated handling.',
      client: 'Tech Conference 2024',
      metric: 'Same week',
    },
    {
      id: 6,
      title: 'Window Graphics',
      category: 'Vinyl Graphics',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=720&h=560&fit=crop',
      description: 'Office glass branding with clean vinyl application and privacy-friendly coverage.',
      client: 'Business Office Complex',
      metric: 'Clean install',
    },
  ];

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter((project) => project.category === activeFilter);

  const handleViewProject = (project) => {
    contactWhatsApp(`Hi! I'm interested in a project like "${project.title}". Could you provide details and pricing?`);
  };

  return (
    <section id="portfolio" className="py-20 lg:py-28 bg-[#F9F9F6] border-b-2 border-[#0A0A0A]" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <SectionHeader
            eyebrow="Proof Of Output"
            title="Finished"
            accent="Work Grid"
            copy="A tighter look at projects that need to be readable from the road, durable outdoors, and clear up close."
          />
          <div className="portfolio-count" data-reveal>
            <span>Showing</span>
            <strong>{String(filteredProjects.length).padStart(2, '0')}</strong>
          </div>
        </div>

        <div className="portfolio-filters" data-testid="portfolio-filters" data-reveal>
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActiveFilter(category)}
              className={activeFilter === category ? 'is-active' : ''}
              data-testid={`portfolio-filter-${category.toLowerCase().replace(/\s/g, '-')}`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="portfolio-grid" data-testid="portfolio-grid">
          {filteredProjects.map((project, index) => (
            <article
              key={project.id}
              className="portfolio-card"
              style={{ '--delay': `${index * 70}ms` }}
              data-testid={`portfolio-item-${project.id}`}
              data-reveal
            >
              <div className="portfolio-image">
                <img
                  src={project.image}
                  alt={project.title}
                  loading="lazy"
                />
                <div className="portfolio-overlay">
                  <button
                    type="button"
                    onClick={() => handleViewProject(project)}
                    aria-label={`Ask about ${project.title}`}
                    data-testid={`portfolio-view-${project.id}`}
                  >
                    <Eye className="h-4 w-4" strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewProject(project)}
                    aria-label={`Open inquiry for ${project.title}`}
                  >
                    <ExternalLink className="h-4 w-4" strokeWidth={2} />
                  </button>
                </div>
                <span className="portfolio-badge">{project.category}</span>
                <span className="portfolio-metric">{project.metric}</span>
              </div>

              <div className="portfolio-body">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div>
                  <span>Client</span>
                  <strong>{project.client}</strong>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed border-[#999]" data-reveal>
            <p className="text-sm font-bold tracking-widest uppercase text-[#666]">No projects in this category</p>
          </div>
        )}

        <div className="text-center mt-16" data-reveal>
          <IndustrialButton
            onClick={() => contactWhatsApp('Hi! I have seen your portfolio and would like to start a new project.')}
            variant="dark"
            testId="portfolio-start-project"
          >
            Start Your Project
          </IndustrialButton>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
